import { SupportedUserRoles } from '@insurancemenu/imenu-components-lib/dist/types';
import {
  brokerRights,
  eeRights,
  employerRights,
  enrollmentRights,
  uwRights,
  zywaveUserRights,
} from '@insurancemenu/imenu-components-lib/dist/utils/accessRights';

export enum GeneralRouteV2Id {
  INITIATE_GROUP = 'INITIATE_GROUP',
  INITIATE_PROPOSAL = 'INITIATE_PROPOSAL',
  GROUP_HUB = 'GROUP_HUB',
  EMPLOYER_PROFILE = 'EMPLOYER_PROFILE',
}

export enum UserSettingsRouteV2Id {
  ACCOUNT_SETTINGS = 'ACCOUNT_SETTINGS',
  NOTIFICATION_SETTINGS = 'NOTIFICATION_SETTINGS',
}

export enum ZywaveRouteV2Id {
  ZYWAVE_GROUP_INFO = 'ZYWAVE_GROUP_INFO',
}

export enum QuoteRouteV2Id {
  QUOTE_PROPOSAL_INFO = 'QUOTE_PROPOSAL_INFO',
  QUOTE_CHECKLIST = 'QUOTE_CHECKLIST',
  QUOTE_ADD_EMPLOYEES = 'QUOTE_ADD_EMPLOYEES',
  QUOTE_ADD_EMPLOYEES_CENSUS_UPLOAD = 'QUOTE_ADD_EMPLOYEES_CENSUS_UPLOAD',
  QUOTE_ADD_EMPLOYEES_CENSUS_REVIEW = 'QUOTE_ADD_EMPLOYEES_CENSUS_REVIEW',
  QUOTE_ADD_EMPLOYEES_FULL_FORM = 'QUOTE_ADD_EMPLOYEES_FULL_FORM',
  INITIATE_SHELF_ADD_QUOTE = 'INITIATE_SHELF_ADD_QUOTE',
  QUOTE_OVERRIDE_RATES = 'QUOTE_OVERRIDE_RATES',
  QUOTE_OVERRIDE_RATES_PLAN = 'QUOTE_OVERRIDE_RATES_PLAN',
  QUOTE_ESTIMATED_COST = 'QUOTE_ESTIMATED_COST',
  ZYWAVE_QUOTE_REDIRECT = 'ZYWAVE_QUOTE_REDIRECT',
  IMQ_ADD_EMPLOYEES = 'IMQ_ADD_EMPLOYEES',
  IMQ_ADD_EMPLOYEES_CENSUS_UPLOAD = 'IMQ_ADD_EMPLOYEES_CENSUS_UPLOAD',
  IMQ_ADD_EMPLOYEES_CENSUS_REVIEW = 'IMQ_ADD_EMPLOYEES_CENSUS_REVIEW',
  IMQ_ADD_EMPLOYEES_FULL_FORM = 'IMQ_ADD_EMPLOYEES_FULL_FORM',
  DOWNLOAD_IMQS = 'DOWNLOAD_IMQS',
  IMQ_ADD_EMPLOYEES_HEALTH_QUESTIONNAIRE_ENTRY = 'IMQ_ADD_EMPLOYEES_HEALTH_QUESTIONNAIRE_ENTRY',
  IMQ_ADD_EMPLOYEES_HEALTH_QUESTIONNAIRE = 'IMQ_ADD_EMPLOYEES_HEALTH_QUESTIONNAIRE',
  IMQ_ADD_EMPLOYEES_HEALTH_QUESTIONNAIRE_MEDICAL_CATEGORY = 'IMQ_ADD_EMPLOYEES_HEALTH_QUESTIONNAIRE_MEDICAL_CATEGORY',
  IMQ_ADD_EMPLOYEES_EMPLOYMENT_DETAILS = 'IMQ_ADD_EMPLOYEES_EMPLOYMENT_DETAILS',
  /**
   * Legacy browsing route, still used when adding a plan from Add Quote.
   * Checklist steps that return INITIATE_SHELF_BROWSING (existing prod checklists) are mapped to Add Quote.
   */
  INITIATE_SHELF_BROWSING = 'INITIATE_SHELF_BROWSING',
}

export enum EnrollmentRouteV2Id {
  EMPLOYER_FORMS = 'EMPLOYER_FORMS',
  // These are not yet reworked
  ENROLLMENT_CHECKLIST = 'ENROLLMENT_CHECKLIST',
}

export enum SubscriberRouteV2Id {
  EMPLOYEE_PROFILE = 'EMPLOYEE_PROFILE',
  SUBSCRIBER_HEALTH_QUESTIONNAIRE_ENTRY = 'SUBSCRIBER_HEALTH_QUESTIONNAIRE_ENTRY',
  SUBSCRIBER_HEALTH_QUESTIONNAIRE = 'SUBSCRIBER_HEALTH_QUESTIONNAIRE',
  SUBSCRIBER_HEALTH_QUESTIONNAIRE_MEDICAL_CATEGORY = 'SUBSCRIBER_HEALTH_QUESTIONNAIRE_MEDICAL_CATEGORY',
  SUBSCRIBER_EMPLOYMENT_DETAILS = 'SUBSCRIBER_EMPLOYMENT_DETAILS',
  SUBSCRIBER_IMQ_SUBSCRIBER_PROFILE = 'SUBSCRIBER_IMQ_SUBSCRIBER_PROFILE',
  SUBSCRIBER_IMQ_SUBSCRIBER_DEPENDENTS = 'SUBSCRIBER_IMQ_SUBSCRIBER_DEPENDENTS',
}

export enum ToolsRouteV2Id {
  TRIGGER_RENEWALS = 'TRIGGER_RENEWALS',
  UPLOAD_ALL_EXHIBITS = 'UPLOAD_ALL_EXHIBITS',
}

/**
 * Same name for type and const mirrors old approach with `enum RouteV2Id`: a type in annotations, an object for `RouteV2Id.GROUP_HUB`.
 * Grouped enums are spread into one object for flat, enum-like access without a single monolithic enum.
 */
export type RouteV2Id =
  | GeneralRouteV2Id
  | EnrollmentRouteV2Id
  | QuoteRouteV2Id
  | ZywaveRouteV2Id
  | UserSettingsRouteV2Id
  | SubscriberRouteV2Id
  | ToolsRouteV2Id;

export const RouteV2Id = {
  ...GeneralRouteV2Id,
  ...QuoteRouteV2Id,
  ...ZywaveRouteV2Id,
  ...EnrollmentRouteV2Id,
  ...UserSettingsRouteV2Id,
  ...SubscriberRouteV2Id,
  ...ToolsRouteV2Id,
};

export interface RouteData {
  /**
   * Page URL address.
   */
  path: string;
  title: string;
  /** The text used by the breadcrumb component. */
  breadcrumb?: string;
  subtitle?: string;
  tutorialLink?: string;
  children?: Node[];
  /**
   * Role check function.
   * This function should take an array of user roles and return a boolean indicating whether the user has access to the route.
   */
  roleCheck?: (userRoles: SupportedUserRoles[]) => boolean;
  exact?: boolean;
}

export const GENERAL_ROUTES: Record<GeneralRouteV2Id, RouteData> = {
  [GeneralRouteV2Id.INITIATE_GROUP]: {
    path: '/initiate-group',
    roleCheck: brokerRights,
    title: 'Initiate Group',
    subtitle: 'Search for an existing group or add a new one to start your proposal',
    breadcrumb: 'Initiate Group',
  },
  [GeneralRouteV2Id.INITIATE_PROPOSAL]: {
    path: '/initiate-proposal/:groupId',
    roleCheck: enrollmentRights,
    title: 'Initiate Proposal',
    subtitle:
      'Provide the required information to initiate the proposal and review available product lines based on your input',
    breadcrumb: 'Initiate Proposal',
  },
  [GeneralRouteV2Id.GROUP_HUB]: {
    path: '/group-hub/:groupId',
    /** Any role that may open group profile from flows that link to /group-v2/:groupId. */
    roleCheck: (userRoles: SupportedUserRoles[]) => {
      return (
        brokerRights(userRoles) ||
        enrollmentRights(userRoles) ||
        employerRights(userRoles) ||
        zywaveUserRights(userRoles)
      );
    },
    title: 'Group',
    subtitle: 'Group',
    breadcrumb: 'Group',
  },
  [GeneralRouteV2Id.EMPLOYER_PROFILE]: {
    path: '/employer-profile/:groupId?',
    // Same as employee profile so role flips during multi-login don't bounce via AppRedirect.
    roleCheck: eeRights,
    title: 'Employer Dashboard',
    breadcrumb: 'Employer ',
  },
};

export const USER_SETTINGS_ROUTES: Record<UserSettingsRouteV2Id, RouteData> = {
  [UserSettingsRouteV2Id.ACCOUNT_SETTINGS]: {
    path: '/account-settings',
    roleCheck: brokerRights,
    title: 'Account Settings',
    subtitle: 'View and update your account information',
    breadcrumb: 'Account Settings',
  },
  [UserSettingsRouteV2Id.NOTIFICATION_SETTINGS]: {
    path: '/notification-settings',
    roleCheck: brokerRights,
    title: 'Notification Settings',
    subtitle: 'The following email notifications can be turned off or on at any time',
    breadcrumb: 'Notification Settings',
  },
};

export const ZYWAVE_ROUTES: Record<ZywaveRouteV2Id, RouteData> = {
  [ZywaveRouteV2Id.ZYWAVE_GROUP_INFO]: {
    path: '/zywave-group/:groupId',
    roleCheck: zywaveUserRights,
    title: 'Zywave Group Info',
    subtitle: 'Zywave Group Info',
    breadcrumb: 'Group Info',
  },
};

export const QUOTE_ROUTES: Record<QuoteRouteV2Id, RouteData> = {
  [RouteV2Id.QUOTE_CHECKLIST]: {
    path: '/quote-checklist/:groupId/:periodId/:cartId',
    roleCheck: enrollmentRights,
    title: 'Proposal Checklist',
    subtitle: 'Step by step guided wizard for group setup and enrollment',
    breadcrumb: 'Checklist',
  },
  [RouteV2Id.QUOTE_PROPOSAL_INFO]: {
    path: '/quote-proposal-info/:groupId/:periodId/:cartId',
    roleCheck: enrollmentRights,
    title: 'Proposal Info',
    subtitle: 'Review and update the required information, and explore available product lines based on your input',
    breadcrumb: 'Proposal Info',
  },
  [RouteV2Id.QUOTE_ADD_EMPLOYEES]: {
    path: '/quote-add-employees/:groupId/:periodId/:cartId',
    roleCheck: enrollmentRights,
    title: 'Add Employees',
    subtitle: 'Add employee demographics and enrollments to understand the estimated costs',
    breadcrumb: 'Add Employees',
  },
  [RouteV2Id.QUOTE_ADD_EMPLOYEES_CENSUS_UPLOAD]: {
    path: '/quote-add-employees/census-upload/:groupId/:periodId/:cartId',
    roleCheck: enrollmentRights,
    title: 'Census Import',
    subtitle: 'Use our template to load the employee Census',
    breadcrumb: 'Census Import',
  },
  [RouteV2Id.QUOTE_ADD_EMPLOYEES_CENSUS_REVIEW]: {
    path: '/quote-add-employees/census-review/:groupId/:periodId/:cartId',
    roleCheck: enrollmentRights,
    title: 'Census Review',
    subtitle: 'Review the census file for the group',
    breadcrumb: 'Census Review',
  },
  [RouteV2Id.QUOTE_ADD_EMPLOYEES_FULL_FORM]: {
    path: '/quote-add-employees/full-form/:groupId/:periodId/:cartId',
    roleCheck: enrollmentRights,
    title: 'Full Employee Form',
    subtitle: 'Add an employee with all required details and optionally include dependents and benefit elections.',
    breadcrumb: 'Full Form',
  },
  [RouteV2Id.ZYWAVE_QUOTE_REDIRECT]: {
    path: '/zywave-quote-redirect/:groupId/:periodId/:cartId',
    roleCheck: zywaveUserRights,
    title: 'Quote Redirect',
    subtitle: 'Quote Redirect',
    breadcrumb: 'Quote Redirect',
  },
  [RouteV2Id.IMQ_ADD_EMPLOYEES]: {
    path: '/imq-add-employees/:groupId/:periodId/:cartId',
    roleCheck: enrollmentRights,
    title: 'Add Employees',
    subtitle: 'Add employee demographics and enrollments to understand the estimated costs',
    breadcrumb: 'Add Employees',
  },
  [RouteV2Id.IMQ_ADD_EMPLOYEES_CENSUS_UPLOAD]: {
    path: '/imq-add-employees/census-upload/:groupId/:periodId/:cartId',
    roleCheck: enrollmentRights,
    title: 'Census Import',
    subtitle: 'Use our template to load the employee Census',
    breadcrumb: 'Census Import',
  },
  [RouteV2Id.IMQ_ADD_EMPLOYEES_CENSUS_REVIEW]: {
    path: '/imq-add-employees/census-review/:groupId/:periodId/:cartId',
    roleCheck: enrollmentRights,
    title: 'Census Review',
    subtitle: 'Review the census file for the group',
    breadcrumb: 'Census Review',
  },
  [RouteV2Id.IMQ_ADD_EMPLOYEES_FULL_FORM]: {
    path: '/imq-add-employees/full-form/:groupId/:periodId/:cartId',
    roleCheck: enrollmentRights,
    title: 'Full Employee Form',
    subtitle: 'Add an employee with all required details.',
    breadcrumb: 'Full Form',
  },
  [RouteV2Id.DOWNLOAD_IMQS]: {
    path: '/download-imqs/:groupId/:periodId/:cartId',
    roleCheck: enrollmentRights,
    title: 'Download IMQs',
    subtitle: 'Download all employee-related documents',
    breadcrumb: 'Download IMQs',
  },
  [RouteV2Id.IMQ_ADD_EMPLOYEES_HEALTH_QUESTIONNAIRE_ENTRY]: {
    path: '/imq-add-employees/health-questionnaire-entry/:groupId/:periodId/:cartId/:subscriberId/:entityId',
    roleCheck: enrollmentRights,
    title: 'Health Questionnaire',
    subtitle: 'We will ask you some detailed questions about the employee medical history',
    breadcrumb: 'Health Questionnaire',
  },
  [RouteV2Id.IMQ_ADD_EMPLOYEES_HEALTH_QUESTIONNAIRE]: {
    path: '/imq-add-employees/health-questionnaire/:groupId/:periodId/:cartId/:subscriberId/:entityId',
    roleCheck: enrollmentRights,
    title: 'Health Questionnaire',
    subtitle: 'Answers help ensure all required health information is captured accurately',
    breadcrumb: 'Health Questionnaire',
  },
  [RouteV2Id.IMQ_ADD_EMPLOYEES_HEALTH_QUESTIONNAIRE_MEDICAL_CATEGORY]: {
    path: '/imq-add-employees/medical-category/:groupId/:periodId/:cartId/:subscriberId/:entityId/:category',
    roleCheck: enrollmentRights,
    title: 'Medical Category',
    subtitle: 'Complete the medical category questionnaire',
    breadcrumb: 'Medical Category',
  },
  [RouteV2Id.IMQ_ADD_EMPLOYEES_EMPLOYMENT_DETAILS]: {
    path: '/imq-add-employees/employment-details/:groupId/:periodId/:cartId/:subscriberId/:entityId',
    roleCheck: enrollmentRights,
    title: 'Employment Details',
    subtitle: 'Enter details about employment which may impact eligibility for coverage',
    breadcrumb: 'Employment Details',
  },
  /**
   * Legacy `/browsing` path kept for adding a plan from Add Quote.
   * Existing prod checklists that still return INITIATE_SHELF_BROWSING open Add Quote instead.
   */
  [RouteV2Id.INITIATE_SHELF_BROWSING]: {
    path: '/browsing',
    roleCheck: enrollmentRights,
    title: 'Browse and Build Shelf Proposal',
    subtitle: 'Select plan configurations to add to a proposal and generate customer outputs',
  },
  [RouteV2Id.INITIATE_SHELF_ADD_QUOTE]: {
    path: '/add-quote/:groupId/:periodId/:cartId',
    roleCheck: enrollmentRights,
    title: 'Add Quote',
    subtitle:
      'Build your quote by adding and managing plans, with estimated costs shown when employee data is available',
    breadcrumb: 'Add Quote',
  },
  [RouteV2Id.QUOTE_OVERRIDE_RATES]: {
    path: '/quote-override-rates/:groupId/:periodId/:cartId',
    roleCheck: uwRights,
    title: 'Override Rates',
    subtitle: 'Choose the product and the specific version where you want to override or adjust the final rates',
    breadcrumb: 'Override Rates',
  },
  [RouteV2Id.QUOTE_OVERRIDE_RATES_PLAN]: {
    path: '/quote-override-rates-plan/:groupId/:periodId/:cartId/:portfolioId/:productLineId/:bcIndex',
    roleCheck: uwRights,
    title: 'Override Plan Rates',
    subtitle: 'Adjust your quoted rates by applying a discount or manually updating the values before submission',
    breadcrumb: 'Override Plan Rates',
  },
  [RouteV2Id.QUOTE_ESTIMATED_COST]: {
    path: '/quote-estimated-cost/:groupId/:periodId/:cartId',
    roleCheck: enrollmentRights,
    title: 'Estimated Cost',
    subtitle: 'Enter employee counts by tier to estimate plan costs',
    breadcrumb: 'Estimated Cost',
  },
};

/**
 *  This map contains the new V2 routes. Please break them down flow-wise: general, rfq, template, etc.
 */
export const ENROLLMENT_ROUTES: Record<EnrollmentRouteV2Id, RouteData> = {
  // TEMPORARY PAGE, NOT FULLY MIGRATED YET
  [RouteV2Id.EMPLOYER_FORMS]: {
    path: '/forms-v2/:groupId/:periodId',
    roleCheck: enrollmentRights,
    title: 'Employer Forms',
    subtitle: 'Fill out required carrier forms',
    breadcrumb: 'Employer Forms',
  },

  // TEMPORARY PAGE, NOT FULLY MIGRATED YET
  [RouteV2Id.ENROLLMENT_CHECKLIST]: {
    path: '/checklist-v2/:periodId',
    roleCheck: enrollmentRights,
    title: 'Checklist',
    subtitle: 'Checklist',
    breadcrumb: 'Checklist',
  },
};

export const SUBSCRIBER_ROUTES: Record<SubscriberRouteV2Id, RouteData> = {
  [RouteV2Id.EMPLOYEE_PROFILE]: {
    path: '/employee/:groupId?',
    roleCheck: eeRights,
    title: 'Benefits Dashboard',
    breadcrumb: 'Employee',
  },
  [RouteV2Id.SUBSCRIBER_HEALTH_QUESTIONNAIRE_ENTRY]: {
    path: '/health-questionnaire-entry/:groupId/:subscriberId/:entityId',
    roleCheck: eeRights,
    title: 'Health Questionnaire',
    subtitle: 'We will ask you some detailed questions about your medical history',
    breadcrumb: 'Health Questionnaire',
  },
  [RouteV2Id.SUBSCRIBER_HEALTH_QUESTIONNAIRE]: {
    path: '/health-questionnaire/:groupId/:subscriberId/:entityId',
    roleCheck: eeRights,
    title: 'Health Questionnaire',
    subtitle: 'Your answers help ensure all required health information is captured accurately',
    breadcrumb: 'Health Questionnaire',
  },
  [RouteV2Id.SUBSCRIBER_HEALTH_QUESTIONNAIRE_MEDICAL_CATEGORY]: {
    path: '/medical-category/:groupId/:subscriberId/:entityId/:category',
    roleCheck: eeRights,
    title: 'Medical Category',
    subtitle: 'Complete the medical category questionnaire',
    breadcrumb: 'Medical Category',
  },
  [RouteV2Id.SUBSCRIBER_EMPLOYMENT_DETAILS]: {
    path: '/subscriber-employment-details/:groupId/:subscriberId/:entityId',
    roleCheck: eeRights,
    title: 'Employment Details',
    subtitle: 'Enter details about your employment which may impact eligibility for coverage',
    breadcrumb: 'Employment Details',
  },
  [RouteV2Id.SUBSCRIBER_IMQ_SUBSCRIBER_PROFILE]: {
    path: '/subscriber-imq-profile/:groupId/:subscriberId/:entityId',
    roleCheck: eeRights,
    title: 'Employee Profile',
    subtitle: 'Fill out the required fields to complete the profile',
    breadcrumb: 'Employee Profile',
  },
  [RouteV2Id.SUBSCRIBER_IMQ_SUBSCRIBER_DEPENDENTS]: {
    path: '/subscriber-imq-dependents/:groupId/:subscriberId/:entityId',
    roleCheck: eeRights,
    title: 'Manage Dependents',
    subtitle: 'Manage dependents associated with your profile',
    breadcrumb: 'Manage Dependents',
  },
};

export const TOOLS_ROUTES: Record<ToolsRouteV2Id, RouteData> = {
  [RouteV2Id.TRIGGER_RENEWALS]: {
    path: '/trigger-renewals',
    roleCheck: enrollmentRights,
    title: 'Trigger Renewals',
    breadcrumb: 'Trigger Renewals',
  },
  [RouteV2Id.UPLOAD_ALL_EXHIBITS]: {
    path: '/upload-all-exhibits',
    roleCheck: enrollmentRights,
    title: 'Upload Exhibits',
    breadcrumb: 'Upload Exhibits',
  },
};

export const ROUTES_CONFIG_V2: Record<RouteV2Id, RouteData> = Object.freeze({
  ...GENERAL_ROUTES,
  ...USER_SETTINGS_ROUTES,
  ...ZYWAVE_ROUTES,
  ...QUOTE_ROUTES,
  ...ENROLLMENT_ROUTES,
  ...SUBSCRIBER_ROUTES,
  ...TOOLS_ROUTES,
});
